import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'common/api';
import { ClientIdentityDocumentMedia } from 'common/api/media';
import { I18nNameSpaces } from 'common/services/i18n';
import useMount from 'common/hooks/useMount';
import dayjs from 'common/services/dayjs';
import { message } from 'common/services/message';

const ACCEPTED_FILE_TYPES = 'application/pdf,image/jpeg,image/png';

// Temporarily hidden.
const isUploadButtonTemporarilyHidden = true;

type Props = {
  transactionId: string;
  clientIndex: 1 | 2;
  disabled?: boolean;
};

function latestDocumentForClient(
  documents: ClientIdentityDocumentMedia[],
  clientIndex: 1 | 2,
): ClientIdentityDocumentMedia | undefined {
  return documents
    .filter((document) => Number(document.meta?.clientIndex) === clientIndex)
    .sort((left, right) => dayjs(right.createdAt).valueOf() - dayjs(left.createdAt).valueOf())[0];
}

export default function ClientIdentityDocumentUpload({ transactionId, clientIndex, disabled }: Props) {
  const { t } = useTranslation(I18nNameSpaces.Common);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [latestDocument, setLatestDocument] = useState<ClientIdentityDocumentMedia | undefined>();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  async function loadDocuments() {
    setIsLoadingList(true);

    try {
      const response = await api.media.listClientIdentityDocuments(transactionId);

      if (response.ok) {
        setLatestDocument(latestDocumentForClient(response.data, clientIndex));
      }
    } finally {
      setIsLoadingList(false);
    }
  }

  useMount(() => {
    void loadDocuments();
  });

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const allowedMimeTypes = new Set(['application/pdf', 'image/jpeg', 'image/png']);

    if (!allowedMimeTypes.has(file.type)) {
      message.error(t('informationsFeature.clientIdentityDocument.uploadInvalidType'));
      return;
    }

    setIsUploading(true);

    try {
      const response = await api.media.uploadClientIdentityDocument(transactionId, clientIndex, file);

      if (response.ok) {
        setLatestDocument(response.data);
        message.success(t('informationsFeature.clientIdentityDocument.uploadSuccess'));
        return;
      }

      message.error(t('informationsFeature.clientIdentityDocument.uploadFailed'));
    } catch {
      message.error(t('informationsFeature.clientIdentityDocument.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  }

  if (isUploadButtonTemporarilyHidden && !latestDocument) {
    return null;
  }

  return (
    <div className="client-identity-document-upload">
      {isUploadButtonTemporarilyHidden ? null : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="client-identity-document-upload__input"
            disabled={disabled || isUploading}
            onChange={handleFileChange}
          />
          <Button
            icon={<UploadOutlined />}
            loading={isUploading}
            disabled={disabled || isLoadingList}
            onClick={handleUploadClick}
          >
            {t('informationsFeature.clientIdentityDocument.uploadButton')}
          </Button>
        </>
      )}
      {latestDocument ? (
        <Alert
          className="client-identity-document-upload__status"
          type="success"
          showIcon
          title={t('informationsFeature.clientIdentityDocument.uploaded')}
          description={t('informationsFeature.clientIdentityDocument.uploadedAt', {
            date: dayjs(latestDocument.createdAt).format('L LT'),
          })}
        />
      ) : null}
    </div>
  );
}
